import { ExpandLess, Send, SmartToy } from '@mui/icons-material';
import { Avatar, Box, Button, IconButton, Paper, TextField, Typography } from '@mui/material';
import type { IAIAssistantComponentProperties } from '@sharedInterfaces/IEmployeeTalkTemplate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import RowElement from '@src/App/Components/General/FormularElements/RowElement/RowElement';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import type { ITemplateComponentProps } from '../TemplateEditor/types';

import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';

interface ChatMessage {
    id: string;
    text: string;
    isUser: boolean;
    timestamp: Date;
}

const ETTAIAssistantComponent: React.FC<ITemplateComponentProps> = (props) => {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;

    const properties = props.component.properties as IAIAssistantComponentProperties;

    const [isExpanded, setIsExpanded] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([
        {
            id: '1',
            text:
                properties.welcomeMessage ||
                'Hallo! Ich bin hier, um Ihnen beim Onboarding zu helfen. Haben Sie Fragen?',
            isUser: false,
            timestamp: new Date(),
        },
    ]);
    const [inputText, setInputText] = useState('');

    const handleSendMessage = () => {
        if (!inputText.trim()) return;

        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            text: inputText,
            isUser: true,
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInputText('');

        // Simulate AI response
        setTimeout(() => {
            const aiResponse: ChatMessage = {
                id: (Date.now() + 1).toString(),
                text: 'Das ist eine sehr gute Frage! Basierend auf dem aktuellen Schritt kann ich Ihnen folgende Hilfe anbieten...',
                isUser: false,
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, aiResponse]);
        }, 1000);
    };

    const renderEditContent = () => (
        <>
            <RowElement title="Assistent Name">
                <TextField
                    fullWidth
                    size="small"
                    value={properties.assistantName}
                    onChange={(e) =>
                        props.onEdit?.({
                            ...props.component,
                            properties: {
                                ...properties,
                                assistantName: e.target.value,
                            },
                        })
                    }
                />
            </RowElement>
            <RowElement title="Willkommensnachricht">
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    value={properties.welcomeMessage}
                    onChange={(e) =>
                        props.onEdit?.({
                            ...props.component,
                            properties: {
                                ...properties,
                                welcomeMessage: e.target.value,
                            },
                        })
                    }
                />
            </RowElement>
            <RowElement title="Chat aktivieren">
                <input
                    type="checkbox"
                    checked={properties.enableChat}
                    onChange={(e) =>
                        props.onEdit?.({
                            ...props.component,
                            properties: {
                                ...properties,
                                enableChat: e.target.checked,
                            },
                        })
                    }
                />
            </RowElement>
            <RowElement title="Formular-Hilfe aktivieren">
                <input
                    type="checkbox"
                    checked={properties.enableFormHelp}
                    onChange={(e) =>
                        props.onEdit?.({
                            ...props.component,
                            properties: {
                                ...properties,
                                enableFormHelp: e.target.checked,
                            },
                        })
                    }
                />
            </RowElement>
        </>
    );

    const renderViewContent = () => (
        <Box>
            {properties.position === 'fixed' ? (
                // Fixed position like OnboardingAgent
                <Paper
                    elevation={3}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        width: isExpanded ? 350 : 60,
                        height: isExpanded ? 400 : 60,
                        borderRadius: isExpanded ? 2 : '50%',
                        zIndex: 1000,
                        overflow: 'hidden',
                    }}
                >
                    {isExpanded ? (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            {/* Header */}
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark' }}>
                                        <SmartToy fontSize="small" />
                                    </Avatar>
                                    <Typography variant="subtitle2">{properties.assistantName}</Typography>
                                </Box>
                                <IconButton size="small" onClick={() => setIsExpanded(false)} sx={{ color: 'white' }}>
                                    <ExpandLess />
                                </IconButton>
                            </Box>

                            {/* Messages */}
                            <Box sx={{ flex: 1, p: 1, overflowY: 'auto' }}>
                                {messages.map((message) => (
                                    <Box
                                        key={message.id}
                                        sx={{
                                            mb: 1,
                                            display: 'flex',
                                            justifyContent: message.isUser ? 'flex-end' : 'flex-start',
                                        }}
                                    >
                                        <Paper
                                            sx={{
                                                p: 1,
                                                maxWidth: '80%',
                                                bgcolor: message.isUser ? 'primary.main' : 'grey.100',
                                                color: message.isUser ? 'white' : 'text.primary',
                                            }}
                                        >
                                            <Typography variant="body2">{message.text}</Typography>
                                        </Paper>
                                    </Box>
                                ))}
                            </Box>

                            {/* Input */}
                            {properties.enableChat && (
                                <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Nachricht eingeben..."
                                            value={inputText}
                                            onChange={(e) => setInputText(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                                        />
                                        <IconButton size="small" onClick={handleSendMessage}>
                                            <Send fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <IconButton
                            onClick={() => setIsExpanded(true)}
                            sx={{
                                width: '100%',
                                height: '100%',
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.dark' },
                            }}
                        >
                            <SmartToy />
                        </IconButton>
                    )}
                </Paper>
            ) : (
                // Inline position
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <SmartToy />
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {properties.assistantName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                KI-Assistent
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {properties.welcomeMessage}
                    </Typography>

                    {properties.enableFormHelp && (
                        <Button variant="outlined" size="small" startIcon={<SmartToy />}>
                            Hilfe zu diesem Schritt
                        </Button>
                    )}
                </Paper>
            )}
        </Box>
    );

    return (
        <ETTBaseTemplateComponent
            {...props}
            renderEditContent={renderEditContent}
            renderViewContent={renderViewContent}
        />
    );
};

export default ETTAIAssistantComponent;
